import { useParams, useNavigate } from "react-router";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { useEncryption } from "@/hooks/useEncryption";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { EmptyState } from "@/components/shared/EmptyState";
import { DateProposer } from "@/components/shared/DateProposer";
import { MarkdownText } from "@/components/shared/MarkdownText";
import { ItemDetailPopover } from "@/components/shared/ItemDetailPopover";
import { showToast } from "@/components/shared/Toast";
import { useConfirm } from "@/components/shared/ConfirmDialog";
import { SessionComments } from "@/components/session/SessionComments";
import { SessionPostmortem } from "@/components/session/SessionPostmortem";
import {
  ArrowLeft, Calendar, Lock, Skull, Sparkles, Check, ChevronRight, ChevronDown, Pencil,
  CheckCircle2, RotateCcw, CalendarX, CheckCircle, ChevronUp,
} from "lucide-react";
import { useState, useEffect, useCallback, useMemo } from "react";

interface RawComment {
  id: string;
  author: "admin" | "client";
  encrypted_text: string;
  nonce: string;
  is_image: boolean;
  created_at: string;
}

interface Review {
  emoji?: string;
  encrypted_feedback?: string;
  nonce?: string;
  submitted_at?: string;
}

interface RedeemedItem {
  _key: string;
  edge_key: string;
  name: string;
  icon: string;
  points: number;
  parent_key?: string | null;
  type: "punishment" | "reward";
  item_type?: "addon" | "scene" | null;
  description?: string;
  redeemed_at: string;
}

interface SessionData {
  _key: string;
  name: string;
  icon: string;
  description: string;
  proposed_dates: string[];
  fixed_date: string | null;
  points: number | null;
  current_points: number;
  created_at: string;
  completed_at?: string | null;
  redeemed: RedeemedItem[];
  comments: RawComment[];
  admin_review: Review | null;
  client_review: Review | null;
}

interface Punishment {
  _key: string;
  name: string;
  icon: string;
  description?: string;
  points: number;
  parent_key?: string | null;
  related_keys?: string[];
  included_keys?: string[];
  item_type?: "addon" | "scene" | null;
}

interface TreeNode {
  item: Punishment;
  children: TreeNode[];
}

function buildDeepTree(items: Punishment[]): TreeNode[] {
  const itemMap = new Map(items.map(i => [i._key, i]));
  const childMap = new Map<string, TreeNode[]>();
  for (const item of items) {
    if (item.parent_key && itemMap.has(item.parent_key)) {
      const siblings = childMap.get(item.parent_key) || [];
      siblings.push({ item, children: [] });
      childMap.set(item.parent_key, siblings);
    }
  }
  function attachChildren(nodes: TreeNode[]) {
    for (const node of nodes) {
      node.children = childMap.get(node.item._key) || [];
      attachChildren(node.children);
    }
  }
  const roots: TreeNode[] = [];
  for (const item of items) {
    if (!item.parent_key || !itemMap.has(item.parent_key)) roots.push({ item, children: [] });
  }
  attachChildren(roots);
  return roots;
}

function PunishmentNode({
  node, depth, selectedKeys, allItems, includedByKeys, collapsedParents, toggleCollapsed, onToggle, onJumpToItem, canAfford, commitInProgress,
}: {
  node: TreeNode; depth: number; selectedKeys: Set<string>; allItems: Map<string, Punishment>;
  includedByKeys: Set<string>; collapsedParents: Set<string>; toggleCollapsed: (key: string) => void; onToggle: (key: string) => void;
  onJumpToItem: (key: string) => void; canAfford: (key: string) => boolean; commitInProgress: boolean;
}) {
  const p = node.item;
  const isSelected = selectedKeys.has(p._key);
  const isIncluded = includedByKeys.has(p._key);
  const isCollapsed = collapsedParents.has(p._key);
  const hasChildren = node.children.length > 0;
  const affordable = canAfford(p._key);
  const canToggle = !commitInProgress && (isSelected || affordable);
  const marginLeft = depth > 0 ? `${Math.min(depth * 1.5, 6)}rem` : undefined;

  let totalCost = isIncluded ? 0 : p.points;
  let ancestorCur = p.parent_key;
  while (ancestorCur) {
    if (!selectedKeys.has(ancestorCur)) { const anc = allItems.get(ancestorCur); if (anc) totalCost += (includedByKeys.has(ancestorCur) ? 0 : anc.points); }
    ancestorCur = allItems.get(ancestorCur)?.parent_key || null;
  }
  const needsParentUnlock = totalCost > (isIncluded ? 0 : p.points);

  return (
    <div data-item-key={p._key}>
      <div style={{ marginLeft }} onClick={() => canToggle && onToggle(p._key)}
        className={`${depth > 0 ? "mt-1" : ""} flex items-center gap-2.5 p-3 rounded-xl border transition-all duration-200 ${
          !canToggle ? "border-app-border/50 bg-app-surface-raised/30 opacity-60 cursor-not-allowed"
            : isSelected ? "border-app-danger/30 bg-app-danger/5 cursor-pointer"
            : "border-app-border hover:border-app-danger/30 hover:bg-app-danger/5 hover:shadow-sm cursor-pointer"
        }`}
        title={!affordable && !isSelected ? "Not enough points" : undefined}>
        {hasChildren && (
          <button type="button" onClick={(e) => { e.stopPropagation(); toggleCollapsed(p._key); }}
            className="w-5 h-5 flex items-center justify-center text-app-text-muted hover:text-app-text transition-colors">
            {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        )}
        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${isSelected ? "bg-app-danger border-app-danger text-white" : "border-app-border"}`}>
          {isSelected && <Check className="w-3 h-3" />}
        </div>
        <div className={`${depth > 0 ? "w-8 h-8 text-base" : "w-9 h-9 text-lg"} rounded-xl flex items-center justify-center ${
          isSelected ? "bg-app-success/10" : p.item_type === "scene" ? "bg-app-info/10" : p.item_type === "addon" ? "bg-app-accent/10" : "bg-app-danger/10"
        }`}>{p.icon}</div>
        <div className="flex-1 min-w-0">
          <p className={`text-body font-medium ${isSelected ? "text-app-success" : ""}`}>{p.name}</p>
          {p.description && (
            <ItemDetailPopover item={p} pointsColor="danger" pointsPrefix="+"
              actions={[
                { label: "Jump to Entry", onClick: () => onJumpToItem(p._key) },
                ...(!isSelected ? [{ label: "Add to Session", onClick: () => onToggle(p._key) }] : []),
              ]}>
              <MarkdownText text={p.description} className="text-info text-app-text-muted truncate" plain truncateAtSplit />
            </ItemDetailPopover>
          )}
          <p className={`text-info font-semibold ${isSelected ? "text-app-success" : "text-app-danger"}`}>
            {isSelected ? (
              isIncluded ? <><span className="line-through opacity-50">+{p.points}</span>{" "}✓ Included (free)</> : "✓ Selected"
            ) : !affordable ? (
              isIncluded ? <><span className="line-through opacity-50">{totalCost + p.points}</span>{" "}⚠ {totalCost} pts</> : `⚠ ${totalCost} pts`
            ) : isIncluded ? (
              <><span className="line-through opacity-50">+{p.points}</span>{" "}+0 pts (included)</>
            ) : needsParentUnlock ? (
              `+${p.points} pts (+${totalCost - p.points} unlock)`
            ) : (
              `+${p.points} pts`
            )}
          </p>
          {p.item_type && (
            <span className={`inline-block text-info px-1.5 py-0 rounded-full font-medium mt-0.5 ${p.item_type === "scene" ? "bg-app-info/10 text-app-info" : "bg-app-accent/10 text-app-accent"}`}>
              {p.item_type === "scene" ? "🎬 Scene" : "🧩 Addon"}
            </span>
          )}
        </div>
      </div>
      {isSelected && p.related_keys && p.related_keys.length > 0 && (() => {
        const relatedItems = p.related_keys!.map(k => allItems.get(k)).filter((r): r is Punishment => r != null && !selectedKeys.has(r._key));
        if (relatedItems.length === 0) return null;
        return (
          <div className="flex flex-wrap gap-1 mt-1.5" style={{ marginLeft }}>
            {relatedItems.map(rel => {
              const relIncluded = includedByKeys.has(rel._key);
              let relCost = relIncluded ? 0 : rel.points; let cur = rel.parent_key;
              while (cur) { if (!selectedKeys.has(cur)) { const p = allItems.get(cur); if (p) relCost += (includedByKeys.has(cur) ? 0 : p.points); } cur = allItems.get(cur)?.parent_key || null; }
              return (
                <ItemDetailPopover key={rel._key} item={rel} pointsColor="danger" pointsPrefix="+"
                  actions={[
                    { label: "Jump to Entry", onClick: () => onJumpToItem(rel._key) },
                    { label: "Add to Session", onClick: () => onToggle(rel._key) },
                  ]}>
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-app-danger/10 hover:bg-app-danger/20 text-app-danger text-info font-medium transition-colors cursor-pointer disabled:opacity-50"
                    title={`Jump to ${rel.name} (+${relCost} pts total)`}>
                    <span className="text-xs">{rel.icon}</span>
                    <span className="truncate max-w-20 text-xs">{rel.name}</span>
                    {relIncluded ? (
                      <><span className="text-xs opacity-50 line-through">+{rel.points}</span><span className="text-xs opacity-70">+0</span></>
                    ) : (
                      <span className="text-xs opacity-70">+{relCost}</span>
                    )}
                  </span>
                </ItemDetailPopover>
              );
            })}
          </div>
        );
      })()}
      {isSelected && p.included_keys && p.included_keys.length > 0 && (() => {
        const includedItems = p.included_keys!.map(k => allItems.get(k)).filter((r): r is Punishment => r != null);
        if (includedItems.length === 0) return null;
        return (
          <div className="flex flex-wrap gap-1 mt-1" style={{ marginLeft }}>
            {includedItems.map(inc => (
              <ItemDetailPopover key={inc._key} item={inc} pointsColor="danger" pointsPrefix="+"
                actions={[
                  { label: "Jump to Entry", onClick: () => onJumpToItem(inc._key) },
                  { label: "Add to Session", onClick: () => onToggle(inc._key) },
                ]}>
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-app-success/10 hover:bg-app-success/20 text-app-success text-info font-medium transition-colors cursor-pointer disabled:opacity-50"
                  title={`Included free: ${inc.name} (${inc.points} pts)`}>
                  <span className="text-xs">🎁</span>
                  <span className="text-xs">{inc.icon}</span>
                  <span className="truncate max-w-20 text-xs">{inc.name}</span>
                  <span className="text-xs opacity-50 line-through">+{inc.points}</span>
                  <span className="text-xs opacity-70">+0</span>
                </span>
              </ItemDetailPopover>
            ))}
          </div>
        );
      })()}
      {hasChildren && !isCollapsed && node.children.map((child) => (
        <PunishmentNode key={child.item._key} node={child} depth={depth + 1} selectedKeys={selectedKeys}
          allItems={allItems} includedByKeys={includedByKeys} collapsedParents={collapsedParents} toggleCollapsed={toggleCollapsed}
          onToggle={onToggle} onJumpToItem={onJumpToItem} canAfford={canAfford} commitInProgress={commitInProgress} />
      ))}
    </div>
  );
}

function formatDisplayDate(dateStr: string): string {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

export function AdminSessionDetail() {
  const { jwt, clientId, sessionId } = useParams();
  const navigate = useNavigate();
  const { getHeaders } = useAuth();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const { decryptFields, decryptList, encryptValue, decryptValue, encryptBinary, decryptBinary, hasKey } = useEncryption(clientId);

  const { data: rawSession, loading, error, refetch } = useApi<SessionData>(
    `/api/admin/${jwt}/${clientId}/sessions/${sessionId}`,
  );
  const { data: rawPunishments } = useApi<Punishment[]>(
    `/api/admin/${jwt}/${clientId}/punishments`,
  );

  const [session, setSession] = useState<SessionData | null>(null);
  const [punishments, setPunishments] = useState<Punishment[] | null>(null);
  const [fixLoading, setFixLoading] = useState(false);
  const [commitLoading, setCommitLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [collapsedParents, setCollapsedParents] = useState<Set<string>>(new Set());
  const [editMode, setEditMode] = useState(false);
  const [itemsCollapsed, setItemsCollapsed] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showReschedule, setShowReschedule] = useState(false);

  const toggleCollapsed = (key: string) => {
    setCollapsedParents(prev => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  };

  useEffect(() => {
    if (!rawSession) return;
    (async () => {
      const dec = await decryptFields(rawSession as unknown as Record<string, unknown>, ["name", "description"]);
      const redeemed = rawSession.redeemed
        ? await decryptList(rawSession.redeemed as unknown as Record<string, unknown>[], ["name", "description"])
        : [];
      setSession({
        ...(dec as unknown as SessionData),
        redeemed: redeemed as unknown as RedeemedItem[],
        comments: rawSession.comments || [],
        admin_review: rawSession.admin_review,
        client_review: rawSession.client_review,
      });
    })();
  }, [rawSession, decryptFields, decryptList]);

  useEffect(() => {
    if (!rawPunishments) return;
    decryptList(rawPunishments as unknown as Record<string, unknown>[], ["name", "description"])
      .then(d => setPunishments(d as unknown as Punishment[]));
  }, [rawPunishments, decryptList]);

  useEffect(() => {
    if (session?.redeemed) setSelectedKeys(new Set(session.redeemed.filter(r => r.type === "punishment").map(r => r._key)));
  }, [session?.redeemed]);

  useEffect(() => { if (session?.completed_at) setItemsCollapsed(true); }, [session?.completed_at]);

  useEffect(() => {
    if (!punishments) return;
    const selected = selectedKeys;
    const hasSelectedDescendant = (key: string): boolean => {
      for (const p of punishments) { if (p.parent_key === key && (selected.has(p._key) || hasSelectedDescendant(p._key))) return true; }
      return false;
    };
    const parentKeys = new Set(punishments.filter(p => p.parent_key).map(p => p.parent_key!));
    const toCollapse = new Set<string>();
    for (const key of parentKeys) { if (!selected.has(key) && !hasSelectedDescendant(key)) toCollapse.add(key); }
    setCollapsedParents(toCollapse);
  }, [punishments, selectedKeys]);

  const redeemedKeys = new Set(session?.redeemed?.filter(r => r.type === "punishment").map(r => r._key) || []);
  const punishmentMap = new Map(punishments?.map(p => [p._key, p]) || []);
  const punishmentTree = punishments ? buildDeepTree(punishments) : [];
  const currentPoints = session?.current_points ?? 0;

  // Compute which keys are "included" (price zeroed) by any currently selected item
  const includedByKeys = useMemo(() => {
    const included = new Set<string>();
    for (const key of selectedKeys) {
      const item = punishmentMap.get(key);
      if (item?.included_keys) { for (const ik of item.included_keys) included.add(ik); }
    }
    return included;
  }, [selectedKeys, punishmentMap]);

  const newlySelected = [...selectedKeys].filter(k => !redeemedKeys.has(k));
  const newlyDeselected = [...redeemedKeys].filter(k => !selectedKeys.has(k));
  const pendingPointChange = newlySelected.reduce((sum, k) => sum + (includedByKeys.has(k) ? 0 : (punishmentMap.get(k)?.points || 0)), 0)
    - newlyDeselected.reduce((sum, k) => sum + (punishmentMap.get(k)?.points || 0), 0);
  const previewBalance = currentPoints + pendingPointChange;
  const hasChanges = newlySelected.length > 0 || newlyDeselected.length > 0;

  // Calculate savings from included items
  const includedSavings = useMemo(() => {
    let savings = 0;
    for (const key of selectedKeys) {
      if (includedByKeys.has(key)) {
        savings += punishmentMap.get(key)?.points || 0;
      }
    }
    return savings;
  }, [selectedKeys, includedByKeys, punishmentMap]);

  const canAffordPunishment = useCallback((key: string): boolean => {
    if (selectedKeys.has(key)) return true;
    let totalCost = includedByKeys.has(key) ? 0 : (punishmentMap.get(key)?.points || 0);
    let cur = punishmentMap.get(key)?.parent_key;
    while (cur) { if (!selectedKeys.has(cur)) totalCost += (includedByKeys.has(cur) ? 0 : (punishmentMap.get(cur)?.points || 0)); cur = punishmentMap.get(cur)?.parent_key || null; }
    return previewBalance + totalCost <= 0;
  }, [selectedKeys, punishmentMap, previewBalance, includedByKeys]);

  const jumpToItem = useCallback((key: string) => {
    setCollapsedParents(prev => {
      const next = new Set(prev);
      let cur = punishmentMap.get(key)?.parent_key;
      while (cur) { next.delete(cur); cur = punishmentMap.get(cur)?.parent_key || null; }
      return next;
    });
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-item-key="${key}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        const inner = el.firstElementChild as HTMLElement | null;
        if (inner) { inner.classList.add("ring-2", "ring-app-danger/50"); setTimeout(() => inner.classList.remove("ring-2", "ring-app-danger/50"), 1500); }
      }
    });
  }, [punishmentMap]);

  // Compute which committed items were "included" (free) by another committed item
  const allRedeemed = useMemo(() => (session?.redeemed || []).filter(r => r.name), [session?.redeemed]);
  const committedIncludedKeys = useMemo(() => {
    const included = new Set<string>();
    const redeemedKeySet = new Set(allRedeemed.map(r => r._key));
    for (const key of redeemedKeySet) {
      const item = punishmentMap.get(key);
      if (item?.included_keys) { for (const ik of item.included_keys) { if (redeemedKeySet.has(ik)) included.add(ik); } }
    }
    return included;
  }, [allRedeemed, punishmentMap]);

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-app-danger text-body p-4">{error}</p>;
  if (!session) return <EmptyState icon="📅" title="Session not found" />;

  const toggleSelection = (key: string) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        const removeDesc = (pk: string) => { for (const p of (punishments || [])) { if (p.parent_key === pk && next.has(p._key)) { next.delete(p._key); removeDesc(p._key); } } };
        removeDesc(key);
      } else {
        next.add(key);
        let cur = punishmentMap.get(key)?.parent_key;
        while (cur) { next.add(cur); cur = punishmentMap.get(cur)?.parent_key || null; }
      }
      return next;
    });
  };

  const handleCommit = async () => {
    if (!hasChanges) return;
    setCommitLoading(true);
    try {
      const unredeemEdgeKeys = newlyDeselected.map(k => session.redeemed?.find(r => r._key === k)?.edge_key).filter(Boolean) as string[];
      const res = await fetch(`/api/admin/${jwt}/${clientId}/sessions/${sessionId}/batch-update`, {
        method: "POST", headers: getHeaders(),
        body: JSON.stringify({ redeem_keys: newlySelected, unredeem_edge_keys: unredeemEdgeKeys }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      showToast.success("Punishments updated!");
      setEditMode(false);
      refetch();
    } catch (err) { showToast.error(err instanceof Error ? err.message : "Failed to update"); }
    finally { setCommitLoading(false); }
  };

  const handleFixDate = async (date: string) => {
    const ok = await confirm({ title: "Fix this date?", description: "This will lock the session date.", confirmLabel: "Fix Date", destructive: false });
    if (!ok) return;
    setFixLoading(true);
    try {
      const res = await fetch(`/api/admin/${jwt}/${clientId}/sessions/${sessionId}/fix-date`, {
        method: "POST", headers: getHeaders(), body: JSON.stringify({ date }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      showToast.success("Date fixed!");
      refetch();
    } catch (err) { showToast.error(err instanceof Error ? err.message : "Failed to fix date"); }
    finally { setFixLoading(false); }
  };

  const handleReschedule = async (date: string) => {
    setActionLoading("reschedule");
    try {
      const res = await fetch(`/api/admin/${jwt}/${clientId}/sessions/${sessionId}/reschedule`, {
        method: "POST", headers: getHeaders(), body: JSON.stringify({ date }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      showToast.success("Session rescheduled!");
      setShowReschedule(false);
      refetch();
    } catch (err) { showToast.error(err instanceof Error ? err.message : "Failed to reschedule"); }
    finally { setActionLoading(null); }
  };

  const handleReopen = async () => {
    const ok = await confirm({ title: "Reopen date polling?", description: "This will clear the fixed date and let the partner propose new dates.", confirmLabel: "Reopen", destructive: true });
    if (!ok) return;
    setActionLoading("reopen");
    try {
      const res = await fetch(`/api/admin/${jwt}/${clientId}/sessions/${sessionId}/reopen`, { method: "POST", headers: getHeaders() });
      if (!res.ok) throw new Error((await res.json()).error);
      showToast.success("Date polling reopened!");
      refetch();
    } catch (err) { showToast.error(err instanceof Error ? err.message : "Failed to reopen"); }
    finally { setActionLoading(null); }
  };

  const handleComplete = async () => {
    const ok = await confirm({ title: "Mark as completed?", description: "This locks the session and opens the postmortem for both parties.", confirmLabel: "Mark Complete", destructive: false });
    if (!ok) return;
    setActionLoading("complete");
    try {
      const res = await fetch(`/api/admin/${jwt}/${clientId}/sessions/${sessionId}/complete`, { method: "POST", headers: getHeaders() });
      if (!res.ok) throw new Error((await res.json()).error);
      showToast.success("Session marked as completed!");
      refetch();
    } catch (err) { showToast.error(err instanceof Error ? err.message : "Failed to mark complete"); }
    finally { setActionLoading(null); }
  };

  const handleSendComment = async (encrypted_text: string) => {
    const res = await fetch(`/api/admin/${jwt}/${clientId}/sessions/${sessionId}/comment`, {
      method: "POST", headers: getHeaders(), body: JSON.stringify({ encrypted_text, nonce: "embedded" }),
    });
    if (!res.ok) throw new Error((await res.json()).error);
    refetch();
  };

  const handleSendImageComment = async (encrypted_text: string) => {
    const res = await fetch(`/api/admin/${jwt}/${clientId}/sessions/${sessionId}/comment`, {
      method: "POST", headers: getHeaders(), body: JSON.stringify({ encrypted_text, nonce: "embedded", is_image: true }),
    });
    if (!res.ok) throw new Error((await res.json()).error);
    refetch();
  };

  const handleSubmitReview = async (emoji: string, encrypted_feedback: string) => {
    const res = await fetch(`/api/admin/${jwt}/${clientId}/sessions/${sessionId}/review`, {
      method: "POST", headers: getHeaders(), body: JSON.stringify({ emoji, encrypted_feedback }),
    });
    if (!res.ok) throw new Error((await res.json()).error);
    showToast.success("Review submitted!");
    refetch();
  };

  const scenes = allRedeemed.filter(r => r.item_type === "scene");
  const addons = allRedeemed.filter(r => r.item_type === "addon");
  const others = allRedeemed.filter(r => !r.item_type);
  const totalItems = allRedeemed.length;

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-xl hover:bg-app-surface-raised">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-app-primary/15 flex items-center justify-center text-xl shrink-0">{session.icon}</div>
          <div className="flex-1 min-w-0">
            <h1 className="text-heading font-bold leading-tight">{session.name}</h1>
            {session.description && <MarkdownText text={session.description} className="text-info text-app-text-muted" />}
            {session.completed_at && (
              <span className="inline-flex items-center gap-1 text-info bg-app-success/15 text-app-success px-2 py-0.5 rounded-full mt-0.5">
                <CheckCircle className="w-3 h-3" /> Completed
              </span>
            )}
          </div>
        </div>
        <Button onClick={() => navigate(`/admin/${jwt}/${clientId}/sessions/${sessionId}/edit`)}
          className="bg-app-primary hover:bg-app-primary-hover shrink-0">
          <Pencil className="w-4 h-4 mr-1.5" /> Edit
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* ── Left Column ── */}
        <div className="space-y-4">
          {/* Date Card */}
          <Card className="border-app-border rounded-2xl overflow-hidden pt-0">
            <CardHeader className="pb-2 bg-app-primary/5 rounded-t-2xl pt-4">
              <CardTitle className="text-body flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-app-primary/15 flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-app-primary" />
                </div>
                <span>Session Date</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              {session.fixed_date ? (
                <>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-app-success/10 border border-app-success/20">
                    <div className="w-8 h-8 rounded-full bg-app-success/20 flex items-center justify-center">
                      <Lock className="w-4 h-4 text-app-success" />
                    </div>
                    <div className="flex-1">
                      <p className="text-body font-bold text-app-success">{formatDisplayDate(session.fixed_date)}</p>
                      <p className="text-info text-app-text-muted">Date is locked</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2.5 rounded-xl bg-app-surface-raised">
                    <Sparkles className="w-4 h-4 text-app-primary" />
                    <span className="text-info text-app-text-muted">Budget at lock:</span>
                    <span className="text-body font-bold text-app-primary">{session.points}</span>
                  </div>
                  <div className={`flex items-center gap-2 p-2.5 rounded-xl ${currentPoints >= 0 ? "bg-app-success/10" : "bg-app-danger/10"}`}>
                    <span className="text-info text-app-text-muted">Current balance:</span>
                    <span className={`text-body font-bold ${currentPoints >= 0 ? "text-app-success" : "text-app-danger"}`}>{currentPoints} pts</span>
                  </div>

                  {!session.completed_at && (
                    <div className="pt-1 space-y-2 border-t border-app-border">
                      <button type="button" onClick={() => setShowReschedule(!showReschedule)}
                        className="flex items-center gap-2 text-info text-app-text-muted hover:text-app-primary transition-colors w-full">
                        <RotateCcw className="w-3.5 h-3.5" />
                        <span>{showReschedule ? "Hide reschedule" : "Reschedule to a different date"}</span>
                        {showReschedule ? <ChevronUp className="w-3.5 h-3.5 ml-auto" /> : <ChevronRight className="w-3.5 h-3.5 ml-auto" />}
                      </button>
                      {showReschedule && (
                        <DateProposer
                          proposedDates={[session.fixed_date]}
                          onPropose={(dates) => {
                            const newDates = dates.filter(d => d !== session.fixed_date);
                            if (newDates.length > 0) handleReschedule(newDates[newDates.length - 1]!);
                          }}
                          disabled={actionLoading === "reschedule"}
                        />
                      )}
                      <button type="button" onClick={handleReopen} disabled={actionLoading === "reopen"}
                        className="flex items-center gap-2 text-info text-app-danger hover:text-app-danger/80 transition-colors w-full disabled:opacity-50">
                        <CalendarX className="w-3.5 h-3.5" />
                        <span>Reopen date polling for partner</span>
                      </button>
                    </div>
                  )}

                  {!session.completed_at && (
                    <Button onClick={handleComplete} disabled={actionLoading === "complete"}
                      className="w-full bg-app-success hover:bg-app-success/90 text-white">
                      <CheckCircle2 className="w-4 h-4 mr-1.5" />
                      {actionLoading === "complete" ? "Marking…" : "Mark as Completed"}
                    </Button>
                  )}
                </>
              ) : (
                <div className="space-y-4">
                  {(session.proposed_dates || []).length > 0 && (
                    <div className="space-y-2">
                      <p className="text-info font-medium text-app-text-muted">📋 Partner proposed dates — click to fix:</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {(session.proposed_dates || []).map((d) => (
                          <button key={d} type="button" disabled={fixLoading} onClick={() => handleFixDate(d)}
                            className="flex items-center gap-2.5 p-3 rounded-xl border border-app-border bg-app-surface-raised hover:bg-app-primary/10 hover:border-app-primary/40 transition-all duration-200 cursor-pointer group disabled:opacity-50">
                            <div className="w-8 h-8 rounded-lg bg-app-primary/10 group-hover:bg-app-primary/20 flex items-center justify-center transition-colors">
                              <Calendar className="w-4 h-4 text-app-primary" />
                            </div>
                            <div className="text-left">
                              <p className="text-sm font-semibold text-app-text">{formatDisplayDate(d)}</p>
                              <p className="text-xs text-app-text-muted">{d}</p>
                            </div>
                            <Lock className="w-3.5 h-3.5 text-app-text-muted ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="border-t border-app-border pt-4">
                    <p className="text-info font-medium text-app-text-muted mb-2">🗓️ Or pick a date to fix directly:</p>
                    <DateProposer
                      proposedDates={session.proposed_dates || []}
                      onPropose={(dates) => {
                        const newDates = dates.filter(d => !(session.proposed_dates || []).includes(d));
                        if (newDates.length > 0) handleFixDate(newDates[newDates.length - 1]!);
                      }}
                      disabled={fixLoading}
                    />
                  </div>
                  <div className="flex items-start gap-2 p-2.5 rounded-xl bg-app-info/10 border border-app-primary/10">
                    <span className="text-sm">⚡</span>
                    <p className="text-info text-app-text-muted">Fixing a date will lock the session for spending.</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Session Notes */}
          <SessionComments
            comments={session.comments || []}
            currentRole="admin"
            encryptValue={encryptValue}
            decryptValue={decryptValue}
            encryptBinary={encryptBinary}
            decryptBinary={decryptBinary}
            hasKey={hasKey}
            onSendText={handleSendComment}
            onSendImage={handleSendImageComment}
          />

          {/* Postmortem */}
          {session.completed_at && (
            <SessionPostmortem
              ownReview={session.admin_review}
              partnerReview={session.client_review}
              ownRole="admin"
              encryptValue={encryptValue}
              decryptValue={decryptValue}
              hasKey={hasKey}
              onSubmit={handleSubmitReview}
            />
          )}
        </div>

        {/* ── Right Column ── */}
        <div className="space-y-4">
          {editMode ? (
            /* Punishment Selector */
            <Card className="border-app-danger/20 rounded-2xl overflow-hidden pt-0">
              <CardHeader className="pb-2 bg-app-danger/5 rounded-t-2xl pt-4">
                <CardTitle className="text-body flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-app-danger/15 flex items-center justify-center">
                    <Skull className="w-4 h-4 text-app-danger" />
                  </div>
                  <span>Select Punishments</span>
                  <span className="ml-auto text-info bg-app-danger/10 px-2 py-0.5 rounded-full text-app-danger">{punishments?.length || 0}</span>
                  <Button size="sm" variant="outline" className="ml-2"
                    onClick={() => { setEditMode(false); setSelectedKeys(new Set(session.redeemed.filter(r => r.type === "punishment").map(r => r._key))); }}>
                    Cancel
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-3">
                <div className={`flex items-center justify-between p-3 rounded-xl mb-3 ${previewBalance === currentPoints ? "bg-app-surface-raised" : "bg-app-warning/10 border border-app-warning/20"}`}>
                  <div>
                    <p className="text-info text-app-text-muted">Balance after changes</p>
                    <p className={`text-lg font-bold ${previewBalance >= 0 ? "text-app-success" : "text-app-danger"}`}>{previewBalance} pts</p>
                  </div>
                  {pendingPointChange !== 0 && (
                    <span className={`text-info font-semibold px-2 py-0.5 rounded-full ${pendingPointChange > 0 ? "text-app-success bg-app-success/10" : "text-app-danger bg-app-danger/10"}`}>
                      {pendingPointChange > 0 ? "+" : ""}{pendingPointChange} pts
                    </span>
                  )}
                </div>
                {punishmentTree.length > 0 ? (
                  <div className="space-y-2">
                    {punishmentTree.map(node => (
                      <PunishmentNode key={node.item._key} node={node} depth={0} selectedKeys={selectedKeys}
                        allItems={punishmentMap} includedByKeys={includedByKeys} collapsedParents={collapsedParents} toggleCollapsed={toggleCollapsed}
                        onToggle={toggleSelection} onJumpToItem={jumpToItem} canAfford={canAffordPunishment} commitInProgress={commitLoading} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-3xl mb-2">✅</p>
                    <p className="text-info text-app-text-muted">No punishments configured</p>
                  </div>
                )}
                {includedSavings > 0 && (
                  <div className="flex items-center justify-between p-2.5 rounded-xl bg-app-success/10 border border-app-success/20 mt-3">
                    <span className="text-info text-app-text-muted">Included items savings</span>
                    <span className="text-info font-semibold text-app-success">
                      <span className="line-through opacity-50">+{includedSavings}</span> → +0 pts
                    </span>
                  </div>
                )}
                {hasChanges && (
                  <div className="mt-4 pt-3 border-t border-app-border flex items-center justify-between">
                    <div className="text-info text-app-text-muted">
                      {newlySelected.length > 0 && <span className="text-app-danger">+{newlySelected.length} new</span>}
                      {newlySelected.length > 0 && newlyDeselected.length > 0 && <span> · </span>}
                      {newlyDeselected.length > 0 && <span className="text-app-text-muted">-{newlyDeselected.length} removed</span>}
                    </div>
                    <Button onClick={handleCommit} disabled={commitLoading} className="bg-app-danger hover:bg-app-danger/90 text-white rounded-xl">
                      {commitLoading ? "Saving…" : "Confirm Selection"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            /* Committed Items View */
            <Card className="border-app-border rounded-2xl overflow-hidden pt-0">
              <CardHeader className="pb-2 bg-app-surface-raised/50 rounded-t-2xl pt-4">
                <CardTitle className="text-body flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-app-accent/15 flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-app-accent" />
                  </div>
                  <span>Committed Items</span>
                  <span className="ml-auto text-info bg-app-surface-raised px-2 py-0.5 rounded-full text-app-text-muted">{totalItems}</span>
                  {session.fixed_date && !session.completed_at && (
                    <Button size="sm" variant="outline" onClick={() => setEditMode(true)}
                      className="ml-2 border-app-primary text-app-primary hover:bg-app-primary/10">
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit Selection
                    </Button>
                  )}
                  {session.completed_at && (
                    <button type="button" onClick={() => setItemsCollapsed(!itemsCollapsed)}
                      className="ml-2 w-7 h-7 rounded-lg flex items-center justify-center text-app-text-muted hover:bg-app-surface-raised transition-colors">
                      {itemsCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                    </button>
                  )}
                </CardTitle>
              </CardHeader>

              {!itemsCollapsed && (
                <CardContent className="pt-4 space-y-4">
                  {totalItems === 0 && (
                    <div className="text-center py-6">
                      <p className="text-3xl mb-2">📋</p>
                      <p className="text-info text-app-text-muted">No items committed yet.</p>
                      {session.fixed_date && !session.completed_at && (
                        <Button size="sm" variant="outline" onClick={() => setEditMode(true)} className="mt-3 border-app-primary text-app-primary">
                          <Pencil className="w-3.5 h-3.5 mr-1" /> Edit Selection
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Categories */}
                  {others.length > 0 && (
                    <div>
                      <p className="text-[11px] font-semibold text-app-text-muted uppercase tracking-wide px-1 mb-2">💀 Categories</p>
                      <div className="flex flex-wrap gap-1.5">
                        {others.map(item => (
                          <span key={item.edge_key} className="inline-flex items-center gap-1 text-[12px] font-medium px-2 py-1 rounded-full border border-app-border bg-app-surface-raised/50">
                            <span>{item.icon}</span>
                            <span>{item.name}</span>
                            {committedIncludedKeys.has(item._key) ? (
                              <><span className="text-app-danger font-semibold line-through opacity-50">{item.points}p</span><span className="text-app-success font-semibold">0p</span></>
                            ) : (
                              <span className="text-app-danger font-semibold">{item.points}p</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Scenes */}
                  {scenes.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-[11px] font-semibold text-app-text-muted uppercase tracking-wide px-1">🎬 Scenes</p>
                      {scenes.map(item => (
                        <div key={item.edge_key} className="rounded-2xl border border-app-info/20 bg-app-info/5 overflow-hidden">
                          <div className="flex items-center gap-3 p-3 pb-2">
                            <div className="w-10 h-10 rounded-xl bg-app-info/15 flex items-center justify-center text-xl shrink-0">{item.icon}</div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-body font-semibold">{item.name}</span>
                                <span className="text-[11px] px-1.5 py-0 rounded-full font-medium bg-app-info/15 text-app-info">🎬 Scene</span>
                              </div>
                              <span className="text-info font-semibold text-app-danger">
                                {committedIncludedKeys.has(item._key) ? (
                                  <><span className="line-through opacity-50">+{item.points}</span> +0 pts</>
                                ) : (
                                  <>+{item.points} pts</>
                                )}
                              </span>
                            </div>
                          </div>
                          {item.description && (
                            <div className="px-4 pb-3">
                              <MarkdownText text={item.description} className="text-info text-app-text-muted" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add-ons */}
                  {addons.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-[11px] font-semibold text-app-text-muted uppercase tracking-wide px-1">🧩 Add-ons</p>
                      {addons.map(item => (
                        <div key={item.edge_key} className="rounded-xl border border-app-accent/20 bg-app-accent/5 p-3 space-y-1.5">
                          <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-xl bg-app-accent/15 flex items-center justify-center text-lg shrink-0">{item.icon}</div>
                            <div className="flex-1 min-w-0">
                              <span className="text-body font-semibold">{item.name}</span>
                              <span className="text-info font-semibold text-app-danger block">
                                {committedIncludedKeys.has(item._key) ? (
                                  <><span className="line-through opacity-50">+{item.points}</span> +0 pts</>
                                ) : (
                                  <>+{item.points} pts</>
                                )}
                              </span>
                            </div>
                          </div>
                          {item.description && <MarkdownText text={item.description} className="text-info text-app-text-muted pl-1" />}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          )}
        </div>
      </div>

      {confirmDialog}
    </div>
  );
}
